import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AlertTriangle } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/Dialog';
import { Button } from '@/components/ui/Button';
import { brandsApi } from './api';
import { eventsApi } from '@/features/events/api';
import { queryKeys } from '@/lib/api/queryKeys';
import { toast } from '@/components/ui/Toast';
import { useT } from '@/lib/i18n/useT';
import type { XkBrand } from '@/lib/api/types';

/**
 * Borrar una marca, con la fricción que corresponde.
 *
 * No es un `ConfirmDialog` corriente a propósito. Una marca no está sola:
 * arrastra sus proyectos, sus eventos, sus estaciones y el inventario que
 * cuelga de ellos. Un «¿seguro?» con dos botones se contesta que sí por
 * costumbre —se contestan veinte al día— y aquí eso significa hacer
 * desaparecer meses de trabajo de una lista.
 *
 * Por eso hace dos cosas que el diálogo genérico no hace:
 *
 *  · **Dice lo que hay dentro**, contado en el momento: «3 proyectos y 12
 *    eventos». Es la diferencia entre borrar una marca vacía de pruebas y la
 *    del cliente, que desde el desplegable se ven exactamente igual.
 *  · **Pide escribir el nombre.** No es burocracia: obliga a leer cuál se está
 *    borrando. El error real no es querer borrar, es borrar la fila de al lado.
 *
 * ## No se destruye nada
 *
 * El borrado es lógico (`status: 'Deleted'`): la marca desaparece de las
 * listas y sus datos siguen en la base. Se dice en el diálogo porque cambia la
 * decisión — quien cree que está destruyendo algo irreversible no lo hace, y
 * quien cree que lo destruye del todo se queda pensando que lo hizo.
 */
export function DeleteBrandDialog({
  brand,
  open,
  onOpenChange,
  onDeleted,
}: {
  brand: XkBrand;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Se dispara tras borrar: quien llama decide a dónde va el usuario. */
  onDeleted: (brand: XkBrand) => void;
}) {
  const t = useT();
  const queryClient = useQueryClient();
  const [escrito, setEscrito] = useState('');

  // Lo que se lleva por delante. Se pide al abrir el diálogo, no antes: es una
  // pregunta cara que sólo importa cuando alguien va a borrar de verdad.
  const proyectosQ = useQuery({
    queryKey: queryKeys.eventGroups.byBrand(brand.id),
    queryFn: () => eventsApi.listGroups({ companyId: brand.id }),
    enabled: open,
  });
  const eventosQ = useQuery({
    queryKey: queryKeys.events.byBrand(brand.id),
    queryFn: () => eventsApi.listEvents({ companyId: brand.id }),
    enabled: open,
  });

  const proyectos = proyectosQ.data?.total ?? proyectosQ.data?.items.length ?? 0;
  const eventos = eventosQ.data?.total ?? eventosQ.data?.items.length ?? 0;
  const contando = proyectosQ.isLoading || eventosQ.isLoading;

  const borrar = useMutation({
    mutationFn: () => brandsApi.remove(brand.id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.brands.all });
      toast({ tone: 'success', title: t('brandDelete.done', { name: brand.name ?? '' }) });
      onOpenChange(false);
      setEscrito('');
      onDeleted(brand);
    },
    onError: (e: Error) =>
      toast({ tone: 'error', title: t('brandDelete.failed'), description: e.message }),
  });

  // Sin espacios de sobra y sin distinguir mayúsculas: se pide leer el nombre,
  // no reproducirlo al carácter.
  const coincide = escrito.trim().toLowerCase() === (brand.name ?? '').trim().toLowerCase();

  return (
    <Dialog
      open={open}
      onOpenChange={(v) => {
        if (!v) setEscrito('');
        onOpenChange(v);
      }}
    >
      <DialogContent className="sm:max-w-[460px]">
        <DialogHeader>
          <div className="mb-3 grid h-11 w-11 place-items-center rounded-full bg-[var(--color-danger-10,rgba(239,68,68,.14))] text-[var(--color-danger-fg,#ef4444)]">
            <AlertTriangle className="h-5 w-5" />
          </div>
          <DialogTitle>{t('brandDelete.title', { name: brand.name ?? '' })}</DialogTitle>
          <DialogDescription>
            {contando
              ? t('brandDelete.counting')
              : t('brandDelete.body', { projects: proyectos, events: eventos })}
          </DialogDescription>
        </DialogHeader>

        <div className="flex flex-col gap-2">
          <label className="text-[11px] font-extrabold uppercase tracking-widest text-[var(--color-xk-text-muted)]">
            {t('brandDelete.typeName', { name: brand.name ?? '' })}
          </label>
          <input
            type="text"
            value={escrito}
            onChange={(e) => setEscrito(e.target.value)}
            autoComplete="off"
            placeholder={brand.name ?? ''}
            className="w-full rounded-lg border border-[var(--color-xk-border)] bg-[var(--color-xk-bg-deep)] px-3 py-2.5 text-sm text-[var(--color-xk-lavender)] placeholder:text-[var(--color-xk-text-muted)] focus:border-[var(--color-accent-ink)] focus:outline-none"
          />
          <p className="text-[11px] leading-snug text-[var(--color-xk-text-muted)]">
            {t('brandDelete.recoverable')}
          </p>
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)} disabled={borrar.isPending}>
            {t('stock.dialog.cancel')}
          </Button>
          <Button
            variant="danger"
            onClick={() => borrar.mutate()}
            disabled={!coincide || borrar.isPending}
          >
            {borrar.isPending ? t('brandDelete.deleting') : t('brandDelete.confirm')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
